/**
 * Release 1 F2 — resolves what a board page shows from three layers, lowest priority first:
 *   1. device prefs (`gridPrefs` sort/layout/width, `boardViewPrefs` density/group-by per board),
 *   2. the active saved view (`?v=<id>`, or the board's default view when the URL names nothing),
 *   3. the URL (`?f=` filters, `?sort=`, `?view=grid|list`) and the device-local override on top of that view.
 * "Dirty" means the effective tuple differs from the active view; "Save view" / "Update view" persist it.
 */
import type { AssetCard, BoardAssetSort, SearchFilters } from '@/lib/api/types';
import type { BoardView } from '@/lib/api/queries';
import type { ViewDensity, ViewGroupBy, ViewOverride } from '@/stores/gridPrefs';
import { compactBoardFilters, decodeBoardFilters, encodeBoardFilters, sameFilters, type BoardFilters } from '@/features/search/predicates/serialize';
import type { BoardCardEntry } from '@/features/grid/types';
import { isPlaceholder } from '@/features/grid/types';

export type ViewLayout = 'grid' | 'list';
export type ViewTuple = {
  filters: BoardFilters;
  sort: BoardAssetSort;
  layout: ViewLayout;
  density: ViewDensity;
  showTitles: boolean;
  groupBy: ViewGroupBy | null;
};

/** `?v=none` opts out of the board's default view. */
export const NO_VIEW = 'none';

export type Resolved = {
  /** the active saved view, if any */
  view: BoardView | null;
  state: ViewTuple;
  dirty: boolean;
  /** true when `view` was chosen because it is the board default and the URL named none */
  implicit: boolean;
};

export function tupleOf(v: BoardView): ViewTuple {
  return { filters: compactBoardFilters(v.filters), sort: v.sort, layout: v.layout, density: v.density, showTitles: v.show_titles, groupBy: v.group_by ?? null };
}

export function sameTuple(a: ViewTuple, b: ViewTuple): boolean {
  return sameFilters(a.filters, b.filters) && a.sort === b.sort && a.layout === b.layout && a.density === b.density && a.showTitles === b.showTitles && a.groupBy === b.groupBy;
}

export function resolveViewState(
  params: URLSearchParams,
  views: readonly BoardView[] | undefined,
  fallback: ViewTuple,
  override: ViewOverride | undefined,
): Resolved {
  const vParam = params.get('v');
  const f = params.get('f');
  let view: BoardView | null = null;
  let implicit = false;
  if (vParam && vParam !== NO_VIEW) view = views?.find((x) => x.id === vParam) ?? null;
  else if (!vParam && f == null && !params.has('sort')) {
    view = views?.find((x) => x.is_default) ?? null;
    implicit = !!view;
  }
  const base = view ? tupleOf(view) : fallback;
  const sortParam = params.get('sort') as BoardAssetSort | null;
  const layoutParam = params.get('view');
  const state: ViewTuple = {
    filters: f != null ? decodeBoardFilters(f) : base.filters,
    sort: sortParam && SORTS.includes(sortParam) ? sortParam : base.sort,
    layout: layoutParam === 'grid' || layoutParam === 'list' ? layoutParam : base.layout,
    density: override?.density ?? base.density,
    showTitles: override?.showTitles ?? base.showTitles,
    groupBy: override?.groupBy !== undefined ? override.groupBy : base.groupBy,
  };
  return { view, state, dirty: view ? !sameTuple(state, tupleOf(view)) : false, implicit };
}

const SORTS: BoardAssetSort[] = ['added_at:desc', 'added_at:asc', 'name:asc', 'name:desc', 'size:desc', 'size:asc', 'created_at:desc', 'created_at:asc', 'sort_order:asc'];

/** URL for "open this view": `v` plus the view's sort/layout so the top bar controls agree with the grid. */
export function viewParams(params: URLSearchParams, view: BoardView | null): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('f');
  next.delete('sort');
  next.delete('view');
  if (view) {
    next.set('v', view.id);
    next.set('sort', view.sort);
    if (view.layout === 'list') next.set('view', 'list');
  } else next.set('v', NO_VIEW);
  return next;
}

/** URL after a filter change: keeps the active view (now dirty) and writes `f`. */
export function filterParams(params: URLSearchParams, filters: BoardFilters, activeView: BoardView | null): URLSearchParams {
  const next = new URLSearchParams(params);
  const enc = encodeBoardFilters(filters);
  if (activeView && sameFilters(activeView.filters, filters)) next.delete('f');
  else if (enc) next.set('f', enc);
  // against a view, "no filters" is itself a change — keep an explicit empty marker
  else if (activeView) next.set('f', '');
  else next.delete('f');
  return next;
}

/** `BoardViewCreate`/`BoardViewUpdate` body for the effective tuple. */
export function tupleBody(t: ViewTuple): { filters: SearchFilters; sort: BoardAssetSort; layout: ViewLayout; density: ViewDensity; show_titles: boolean; group_by: ViewGroupBy | null } {
  // the generated type reuses `SearchFilters`; the API (and the shared Zod `BoardFilters`) rejects board-scope keys with 422, so none are sent
  return { filters: compactBoardFilters(t.filters) as unknown as SearchFilters, sort: t.sort, layout: t.layout, density: t.density, show_titles: t.showTitles, group_by: t.groupBy };
}

// --- grouping --------------------------------------------------------------------------------------
export type CardGroup = { key: string; label: string; cards: BoardCardEntry[] };

const TYPE_LABEL: Record<string, string> = { image: 'Images', video: 'Videos', audio: 'Audio', document: 'Documents', other: 'Other' };
const APPROVAL_LABEL: Record<string, string> = { none: 'Not reviewed', in_progress: 'In progress', needs_review: 'Needs review', approved: 'Approved' };
const TYPE_ORDER = ['image', 'video', 'audio', 'document', 'other'];
const APPROVAL_ORDER = ['needs_review', 'in_progress', 'approved', 'none'];

/** Splits loaded cards into labelled groups (upload placeholders stay in the first group). Order follows the enum, or first appearance. */
export function groupCards(cards: readonly BoardCardEntry[], by: ViewGroupBy | null, memberName: (id: string | null | undefined) => string, now = new Date()): CardGroup[] | null {
  if (!by) return null;
  const groups = new Map<string, CardGroup>();
  const add = (key: string, label: string, c: BoardCardEntry) => {
    let g = groups.get(key);
    if (!g) {
      g = { key, label, cards: [] };
      groups.set(key, g);
    }
    g.cards.push(c);
  };
  for (const c of cards) {
    if (isPlaceholder(c)) {
      add('__uploads', 'Uploading', c);
      continue;
    }
    const card = c as AssetCard;
    if (by === 'type') add(card.type, TYPE_LABEL[card.type] ?? card.type, c);
    else if (by === 'approval') add(card.approval_status, APPROVAL_LABEL[card.approval_status] ?? card.approval_status, c);
    else if (by === 'uploaded_by') add(card.created_by ?? '__unknown', memberName(card.created_by), c);
    else {
      const b = dateBucket(card.added_at ?? card.created_at, now);
      add(b.key, b.label, c);
    }
  }
  const order = (g: CardGroup) => (g.key === '__uploads' ? -1 : by === 'type' ? TYPE_ORDER.indexOf(g.key) : by === 'approval' ? APPROVAL_ORDER.indexOf(g.key) : by === 'date_added' ? DATE_ORDER.indexOf(g.key) : 0);
  return [...groups.values()].sort((a, b) => order(a) - order(b) || a.label.localeCompare(b.label));
}

const DATE_ORDER = ['today', 'yesterday', 'week', 'month', 'year', 'older'];
function dateBucket(iso: string, now: Date): { key: string; label: string } {
  const d = new Date(iso);
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const t = d.getTime();
  if (t >= startOfDay) return { key: 'today', label: 'Today' };
  if (t >= startOfDay - 86400e3) return { key: 'yesterday', label: 'Yesterday' };
  if (t >= startOfDay - 6 * 86400e3) return { key: 'week', label: 'This week' };
  if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) return { key: 'month', label: 'This month' };
  if (d.getFullYear() === now.getFullYear()) return { key: 'year', label: 'Earlier this year' };
  return { key: 'older', label: 'Older' };
}
